import * as THREE from 'three';
import { Box3, Euler, Vector2, Vector3 } from 'three';
import { generateUUID } from 'three/src/math/MathUtils';
import { Granit } from 'src/services/granits';
import { PieceType } from '../services/pieces';

// --- Types ---

export type FaceKey = 'front' | 'back' | 'left' | 'right';

export interface FaceDetails {
  faceX: number;
  faceX2: number;
  faceY: number;
  faceY2: number;
  faceZ: number;
  faceZ2: number;
}

export interface Dimensions {
  x: number;
  y: number;
  z: number;
  size: number;
}

export type HistoryMap = {
  [k: string]: {
    [k: string]: Float32Array;
  };
};

export type PatternsMap = {
  [key: string]: { [key: string]: any }[];
};

export type PieceDetail = {
  id?: number;
  name: string;
  size: number;
  ttcPrice: number;
  htPrice: number;
  purchasePrice: number;
  type: PieceType;
  x: number;
  y: number;
  z: number;
  granit?: Granit;
  underAssemblyUUID?: string;
  geometry: any;
  position: Vector3;
  uuid?: string;
  hasBeenModified?: boolean;
  stockId?: string;
  stockType?: string;
  slope?: {
    hasSlope: boolean;
    orientation: 'x' | 'z';
    slopeStart: number;
    slopeEnd: number;
    mode: 'closoir' | 'double closoir' | 'tete';
  };
};

// --- Fonctions utilitaires de base ---

export const getFaceName = (key: FaceKey): string => {
  switch (key) {
    case 'front': {
      return 'avant';
    }
    case 'back': {
      return 'arrière';
    }
    case 'left': {
      return 'gauche';
    }
    case 'right': {
      return 'droit';
    }
  }
};

export const toFloat = (value: any): number => {
  return Number.parseFloat(value as string);
};

export const ZERO_FACE_DETAILS: FaceDetails = {
  faceX: 0,
  faceX2: 0,
  faceY: 0,
  faceY2: 0,
  faceZ: 0,
  faceZ2: 0,
};

export const makeExtrudeSettings = (depth: number) => ({
  curveSegments: 12,
  steps: 2,
  depth,
  bevelEnabled: false,
});

/**
 * Calcule la bounding box à partir d'un tableau de positions (sans offset).
 */
export const computeBoundingBox = (positionArray: ArrayLike<number>): Box3 => {
  return new Box3().setFromArray(
    Array.from(positionArray).map(
      (element: number, index: number) => {
        if (index % 3 === 0) return element;
        if (index % 3 === 1) return element;
        if (index % 3 === 2) return element;
        return element;
      },
    ),
  );
};

/**
 * Calcule la bounding box à partir d'un tableau de positions avec un offset de position.
 */
export const computeBoundingBoxWithOffset = (
  positionArray: ArrayLike<number>,
  position: { x: number; y: number; z: number },
): Box3 => {
  return new Box3().setFromArray(
    Array.from(positionArray).map(
      (element: number, index: number) => {
        if (index % 3 === 0) return element + position.x;
        if (index % 3 === 1) return element + position.y;
        if (index % 3 === 2) return element + position.z;
        return element;
      },
    ),
  );
};

/**
 * Extrait les dimensions (largeur, hauteur, profondeur) et le volume depuis une bounding box.
 */
export const getDimensions = (box: Box3): Dimensions => {
  const x = box.max.x - box.min.x;
  const y = box.max.y - box.min.y;
  const z = box.max.z - box.min.z;
  const size = x * y * z;
  return { x, y, z, size };
};

// --- Helpers pour les patterns ---

/**
 * Retourne l'Euler de rotation pour une orientation de pattern donnée.
 */
export const getRotation = (orientation: string): Euler => {
  if (orientation.includes('y')) {
    return new Euler(
      0,
      orientation.includes('inverse') ? Math.PI : 0,
      0,
    );
  }
  return new Euler(0, 0, 0);
};

/**
 * Crée l'entrée d'orientation d'un pattern (canvas, texture, caméra).
 */
export const createPatternOrientation = (el: any) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const imageObj = new Image();
  imageObj.src = el.image;

  imageObj.onload = () => {
    canvas.width = imageObj.width;
    canvas.height = imageObj.height;
    if (context) context.drawImage(imageObj, 0, 0);
  };

  const newCamera = new THREE.PerspectiveCamera();
  newCamera.matrix.fromArray(JSON.parse(el.camera));
  newCamera.matrix.decompose(
    newCamera.position,
    newCamera.quaternion,
    newCamera.scale,
  );

  return {
    id: el.orientation,
    texture: new THREE.CanvasTexture(canvas),
    elements: [],
    camera: newCamera,
  };
};

/**
 * Crée un élément de pattern individuel.
 * Note : leaderGranitEngraving est inclus dans tous les cas.
 * Pour les stèles, la valeur sera simplement undefined.
 */
export const createPatternElement = (
  el: any,
  pieceUUID: string,
  bucketUrl: string,
) => ({
  uuid: generateUUID(),
  patternId: el.pattern?.id,
  imageUrl: `${bucketUrl}/${el.pattern?.image}`,
  reducedImageUrl: `${bucketUrl}/${el.pattern?.reducedImage}`,
  pieceUUID,
  orientation: el.orientation,
  position: new Vector3(el.positionX, el.positionY, 0),
  rotation: getRotation(el.orientation),
  scale: new Vector2(el.scaleX, el.scaleY),
  inverse_horizontal: false,
  inverse_vertical: false,
  name: el.pattern?.name,
  type: el.type,
  color: el.color,
  fontFamily: el.fontFamily,
  fontSize: el.fontSize,
  letterSpacing: String(el.letterSpacing),
  text: el.text,
  align: el.align,
  description: el.pattern?.description,
  default: new Vector2(
    toFloat(
      Math.round(
        (el.pattern?.height ?? 1) *
          (Number.parseFloat(String(el.pattern?.ratio)) ?? 1),
      ),
    ),
    toFloat(el.pattern?.height),
  ),
  price: el.pattern?.price,
  leaderGranitEngraving: el.pattern?.leaderGranitEngraving,
  user: el.pattern?.user,
});

/**
 * Peuple les patterns pour une entité donnée (pièce, sous-assemblage, stèle).
 * Mutate newPatterns en place.
 */
export const populatePatternsForEntity = (
  newPatterns: { [key: string]: { [key: string]: any }[] },
  entityId: string,
  patternsData: any[],
  bucketUrl: string,
) => {
  if (!entityId) return;

  for (const el of patternsData) {
    if (!newPatterns[entityId]) {
      newPatterns[entityId] = [] as any;
    }

    if (!newPatterns[entityId][el.orientation]) {
      newPatterns[entityId][el.orientation] = createPatternOrientation(el);
    }

    newPatterns[entityId][el.orientation].elements?.push(
      createPatternElement(el, entityId, bucketUrl),
    );
  }
};

// --- Helpers divers ---

export const isBetween = (num: number, a: number, b: number): boolean => {
  const min = Math.min.apply(Math, [a, b]);
  const max = Math.max.apply(Math, [a, b]);
  return num >= min && num <= max;
};
